// v2 storage — a granary on staddle stones: a raised timber storehouse standing
// on six mushroom-capped staddles (the rat-proof piers real granaries used),
// reached by a plank ramp to its loading door, with a lean-to sheltering grain
// sacks on one side and an open cart-shed yard with stacked goods at the front.
// Footprint-native 2x2. Ported 1:1 from age-of-mcp's v2/storage.ts, using the
// library's shared `foundationSlab`, `barrel`, `crate` and `village` (TIMBER,
// buildScaffold, gableRoof, sack) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, barrel, crate, village, type Part } from "../../lib";

const BASE = 0.14;
const GX = -0.42;            // the granary stands on the back-left of the plot
const GZ = -0.30;
const BODY_W = 0.95, BODY_D = 0.80;
const PLAT_TOP = 0.42;       // top of the raised floor platform
const WOOD = "#8a6a40";      // granary planking
const WOOD_DARK = "#6e5230"; // platform & trim
const STONE = "#8d8d8d";

// Six staddle stones — a stem with a wide mushroom cap — under the platform.
function staddles(): Part[] {
  const out: Part[] = [];
  for (const [dx, dz] of [
    [-0.40, -0.30], [0.40, -0.30], [-0.40, 0.30], [0.40, 0.30], [0, -0.30], [0, 0.30],
  ] as const) {
    out.push(
      { geometry: new THREE.CylinderGeometry(0.05, 0.06, 0.16, 5), color: STONE,
        position: [GX + dx, BASE + 0.08, GZ + dz] },
      { geometry: new THREE.CylinderGeometry(0.10, 0.10, 0.05, 6), color: "#a0a0a0",
        position: [GX + dx, BASE + 0.185, GZ + dz] },
    );
  }
  return out;
}

// The raised floor platform the storehouse stands on.
function platform(): Part {
  return { geometry: new THREE.BoxGeometry(1.08, 0.07, 0.92), color: WOOD_DARK,
    position: [GX, PLAT_TOP - 0.035, GZ] };
}

// The plank storehouse body, `h` tall on the platform, with corner trims.
function body(h: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(BODY_W, h, BODY_D), color: WOOD,
      position: [GX, PLAT_TOP + h / 2, GZ] },
  ];
  for (const [dx, dz] of [[-0.46, -0.385], [0.46, -0.385], [-0.46, 0.385], [0.46, 0.385]] as const) {
    out.push({ geometry: new THREE.BoxGeometry(0.06, h, 0.06), color: WOOD_DARK,
      position: [GX + dx, PLAT_TOP + h / 2, GZ + dz] });
  }
  if (h > 0.4) {
    out.push({ geometry: new THREE.BoxGeometry(BODY_W + 0.02, 0.05, BODY_D + 0.02),
      color: WOOD_DARK, position: [GX, PLAT_TOP + 0.30, GZ] });
  }
  return out;
}

// The open cart-shed posts at the front-right of the yard.
function shedPosts(): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[0.18, 0.30], [0.88, 0.30], [0.18, 0.86], [0.88, 0.86]] as const) {
    out.push({ geometry: new THREE.BoxGeometry(0.05, 0.5, 0.05), color: WOOD_DARK,
      position: [x, BASE + 0.25, z] });
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) {
    return [foundationSlab(1.94), ...staddles(), platform(),
      ...village.buildScaffold(1.15, 1.0, 0.75, GX, GZ)];
  }
  if (s === 2) {
    return [foundationSlab(1.94), ...staddles(), platform(), ...body(0.58),
      ...shedPosts(), ...village.buildScaffold(1.15, 1.0, 1.3, GX, GZ)];
  }

  const wallH = 0.58;
  const top   = PLAT_TOP + wallH;        // 1.0
  const front = GZ + BODY_D / 2;         // 0.10 — the loading-door face
  return [
    foundationSlab(1.94),
    ...staddles(),
    platform(),
    ...body(wallH),
    // Gable roof over the storehouse.
    ...village.gableRoof({ cx: GX, cz: GZ, topY: top, span: BODY_D, length: BODY_W, rise: 0.5 }),
    // Loading door with a cream lintel, up on the platform.
    { geometry: new THREE.BoxGeometry(0.30, 0.34, 0.05), color: "#553c2b",
      position: [GX, 0.62, front + 0.025] },
    { geometry: new THREE.BoxGeometry(0.36, 0.05, 0.05), color: "#e6dcc2",
      position: [GX, 0.82, front + 0.025] },
    // Plank ramp from the yard up to the platform edge.
    { geometry: new THREE.BoxGeometry(0.24, 0.04, 0.53), color: WOOD,
      position: [GX, 0.28, 0.325], rotation: [0.557, 0, 0] },
    // Lean-to on the granary's east side sheltering grain sacks.
    { geometry: new THREE.BoxGeometry(0.617, 0.04, 0.95), color: "#7d6238",
      position: [0.345, 0.74, GZ], rotation: [0, 0, -0.471] },
    { geometry: new THREE.BoxGeometry(0.05, 0.46, 0.05), color: village.TIMBER,
      position: [0.58, 0.37, GZ - 0.40] },
    { geometry: new THREE.BoxGeometry(0.05, 0.46, 0.05), color: village.TIMBER,
      position: [0.58, 0.37, GZ + 0.40] },
    ...village.sack(0.28, -0.50, BASE),
    ...village.sack(0.46, -0.28, BASE),
    ...village.sack(0.34, -0.08, BASE),
    // Open cart-shed yard at the front-right with stacked goods.
    ...shedPosts(),
    { geometry: new THREE.BoxGeometry(0.86, 0.04, 0.74), color: "#7d6238",
      position: [0.53, 0.67, 0.58], rotation: [0.16, 0, 0] },
    crate(0.40, 0.55, 0.30),
    barrel(0.72, 0.62),
    ...village.sack(0.32, 0.74, BASE),
  ];
}

export const storageV2 = defineModel({
  id: "storage-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Storage (v2)",
  tags: ["building","storage","low-poly","age-of-mcp","fable"],
  summary: "Low-poly storage.",
  description: "A walled yard of stacked crates under slanted plank roofs, holding far more goods than a chest.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
